import { useEffect, useRef, useState } from "react";
import { Filing } from "../../../../../../models/Filing";
import { FilingSection } from "../../../../../../models/FilingSection";
import { Button } from "../../../../../../design/button";
import { Switch, SwitchField } from "../../../../../../design/switch";
import { Textarea } from "../../../../../../design/textarea";
import { Card } from "../../../../../../design/card";
import { FilingService } from "../../../../../../services/FilingService";
import { Label } from "../../../../../../design/fieldset";
import {
  LockOpenIcon,
  LockClosedIcon,
  Bars3Icon,
} from "@heroicons/react/20/solid";
import SectionCommentListItem from "../SectionCommentListItem/SectionCommentListItem";
import { Text } from "../../../../../../design/text";
import { Input } from "../../../../../../design/input";
import { Subheading } from "../../../../../../design/heading";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import SlateTextArea, {
  SlateEditorRef,
} from "../../../../../../components/SlateTextArea/SlateTextArea";
import { Descendant } from "slate";
import DeleteFilingSectionButton from "./DeleteFilingSectionButton";
import { get } from "http";
import { getPlainText } from "../../../../../../utils/slate";

interface Props {
  filing: Filing;
  section: FilingSection;
  dragHandleProps: DraggableProvidedDragHandleProps | null;
}

const SectionListItem: React.FC<Props> = ({
  section,
  filing,
  dragHandleProps,
}) => {
  const editorRef = useRef<SlateEditorRef | null>(null);
  const [comment, setComment] = useState<Descendant[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(section.title);
  const [editedContent, setEditedContent] = useState<Descendant[]>(
    section.content ? JSON.parse(section.content) : [],
  );

  const aiTips = [
    // "Clarify the timeline in paragraph 2.",
    // "You might want to expand on who was present during the event.",
  ];

  const handleAddComment = async () => {
    if (!comment) return;
    setLoading(true);
    try {
      await FilingService.addComment(filing._id, section._id!, {
        content: JSON.stringify(comment),
      });
      setComment(null);
      editorRef.current?.resetEditor();
    } catch (err) {
      console.error("Error adding comment:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEditing) {
      console.log("Editing section content...");
    } else if (section.content !== JSON.stringify(editedContent)) {
      console.log("Saving section content...");
      handleSaveContent();
    }
  }, [isEditing]);

  const handleSaveContent = async () => {
    try {
      await FilingService.updateSection(filing._id, section._id!, {
        content: JSON.stringify(editedContent),
        description: getPlainText(editedContent ?? []),
      });
      setIsEditing(false);
    } catch (err) {
      console.error("Error saving section content:", err);
    }
  };

  return (
    <Card key={section._id}>
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {/* Drag handle icon with cursor set to grab */}
            <div
              {...dragHandleProps}
              className="p-1 rounded cursor-grab hover:bg-gray-100"
              title="Drag to reorder"
            >
              <Bars3Icon className="w-5 h-5 text-gray-500" />
            </div>

            {isEditing ? (
              <Input
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="w-24"
              />
            ) : (
              <Subheading>{section.title}</Subheading>
            )}
          </div>

          <div className="flex items-center gap-2">
            <SwitchField>
              <Label>
                {isEditing ? (
                  <LockOpenIcon className="w-4 h-4" />
                ) : (
                  <LockClosedIcon className="w-4 h-4" />
                )}
              </Label>
              <Switch onClick={() => setIsEditing(!isEditing)} />
            </SwitchField>
            <DeleteFilingSectionButton
              filingId={filing._id}
              sectionId={section._id!}
            />
          </div>
        </div>

        <SlateTextArea
          id={section._id!}
          placeholder="Write a reply..."
          ref={editorRef}
          initialValue={section.content ? JSON.parse(section.content) : []}
          readOnly={!isEditing}
          onChange={(content) => setEditedContent(content)}
        />

        <div className="space-y-2">
          <h5 className="text-sm font-medium">Response</h5>
          {section.comments.map((c) => (
            <SectionCommentListItem
              key={c._id}
              filing={filing}
              section={section}
              comment={c}
            />
          ))}
        </div>

        <div className="mt-2">
          <SlateTextArea
            id={section._id!}
            placeholder="Add a comment..."
            ref={editorRef}
            initialValue={comment ?? undefined}
            onChange={(content) => setComment(content)}
          />
          <div className="flex justify-end mt-2">
            <Button
              onClick={handleAddComment}
              disabled={loading || comment?.length === 0}
            >
              {loading ? "Saving..." : "Add Comment"}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SectionListItem;
